import _taggedTemplateLiteral from "@babel/runtime/helpers/taggedTemplateLiteral";
import _isNumber from "lodash/isNumber";
import _capitalize from "lodash/capitalize";
import _kebabCase from "lodash/kebabCase";
import _isEmpty from "lodash/isEmpty";
import _isNil from "lodash/isNil";

var _templateObject;

import "core-js/modules/es6.array.filter.js";
import "core-js/modules/es6.array.map.js";
import React, { Fragment } from 'react';
import classNames from 'classnames';
import { gql } from '@nerdwallet/apollo';
import { Impression } from '@nerdwallet/analytics/react/impression';
import classes from '@nerdwallet/base-styles/classes';
import { useDeprecatedPropsWarning } from '@nerdwallet/deprecate';
import { CREDIT_DASHBOARD_PATH_WITH_TRK, approvalOddsPopoverContent, getColorAndLabel } from '@nerdwallet/likelihood-of-approval';
import { GraphQlMarketplaceStarRating, LAYOUTS, TOOLTIP_TEXT } from '@nerdwallet/react-marketplace-star-rating';
import { VALUE_SIZES } from '@nerdwallet/marketplace/constants/drivers';
import { getCreditScore } from '@nerdwallet/js-auth-tools/selectors';
import QuickFacts, { QuickFactValue } from '@nerdwallet/react-quick-facts';
import { Tooltip, Type } from '@nerdwallet/currency';
import styles from "./keto-product-card.module.css";
import LikelihoodOfApprovalCTA from '../likelihood-of-approval-cta';
import { GET_YOUR_FREE_CREDIT_SCORE, SEE_YOUR_APPROVAL_ODDS, TRK_PRODUCT_LOCATION } from '../lib/constants';
import { safeStructuredContentRender } from '../lib/utils/render';
import { getLoaTooltipContent } from '../lib/utils';
import { CreditScoreWidget } from '../creditscore_pill/CreditScoreWidget';
var TRANSUNION_LOGO_CTA = /*#__PURE__*/React.createElement("a", {
  href: CREDIT_DASHBOARD_PATH_WITH_TRK,
  target: "_",
  className: classes('display-block')
}, /*#__PURE__*/React.createElement("div", {
  className: classNames('tu-logo', styles.tuLogo)
}));

var KetoQuickFacts = function KetoQuickFacts(_ref) {
  var analytics = _ref.analytics,
      enableAnalytics = _ref.enableAnalytics,
      quickFacts = _ref.quickFacts;
  useDeprecatedPropsWarning({
    component: 'KetoQuickFacts',
    deprecatedProps: [{
      version: '6.9.0',
      deprecated: 'enableAnalytics',
      migration: 'Use analytics instead.'
    }],
    props: {
      enableAnalytics: enableAnalytics
    }
  });
  var quickFactComponents = [];
  var filteredQuickFacts = quickFacts.filter(function (quickFact) {
    return quickFact && !_isNil(quickFact.value);
  });

  if (_isEmpty(filteredQuickFacts)) {
    return null;
  }

  filteredQuickFacts.forEach(function (quickFact) {
    quickFactComponents.push( /*#__PURE__*/React.createElement("div", {
      key: "".concat(quickFact.id, "-value"),
      className: classNames(styles.driversRow)
    }, /*#__PURE__*/React.createElement("div", {
      className: classNames([styles.cell, styles.label])
    }, quickFact.labelTooltip ? /*#__PURE__*/React.createElement(Tooltip, {
      id: quickFact.id,
      label: quickFact.label,
      content: /*#__PURE__*/React.createElement("span", {
        className: styles.tooltipContent
      }, quickFact.labelTooltip),
      title: "More information about ".concat(quickFact.label)
    }) : quickFact.label), /*#__PURE__*/React.createElement("div", {
      className: classNames([styles.cell, styles.value])
    }, /*#__PURE__*/React.createElement(QuickFactValue, {
      analytics: analytics,
      enableAnalytics: enableAnalytics || !_isEmpty(analytics),
      quickFact: quickFact
    }))));
  });
  return quickFactComponents;
};

export var GqlKetoCardDrivers = function GqlKetoCardDrivers(_ref2) {
  var analytics = _ref2.analytics,
      approvalLikelihood = _ref2.approvalLikelihood,
      approvalOddsEnabled = _ref2.approvalOddsEnabled,
      className = _ref2.className,
      driversOverrides = _ref2.driversOverrides,
      gqlMarketplaceEntity = _ref2.gqlMarketplaceEntity,
      identity = _ref2.identity,
      legacyTrkProps = _ref2.legacyTrkProps,
      omitRating = _ref2.omitRating,
      reviewLinkOverride = _ref2.reviewLinkOverride,
      showMatchScoreCTA = _ref2.showMatchScoreCTA,
      showNotYetRated = _ref2.showNotYetRated,
      shouldOmitCreditScoreDriver = _ref2.shouldOmitCreditScoreDriver,
      showCreditScorePill = _ref2.showCreditScorePill;

  /*
   * @todo https://nerdwallet.atlassian.net/browse/RUR-532 to get the actual matchScore
   * Leaving this for now because the field will be defined in the styleguide but will be undefined
   * for the client.
   */
  var creditScoreComparisonContent = gqlMarketplaceEntity.creditScoreComparisonContent,
      matchScore = gqlMarketplaceEntity.matchScore,
      maxFico = gqlMarketplaceEntity.maxFico,
      minFico = gqlMarketplaceEntity.minFico,
      placementDrivers = gqlMarketplaceEntity.placementDrivers,
      recommendedCreditScoreRangeDetails = gqlMarketplaceEntity.recommendedCreditScoreRangeDetails,
      starRating = gqlMarketplaceEntity.starRating,
      limitedCreditHistory = gqlMarketplaceEntity.limitedCreditHistory;

  if (_isEmpty(placementDrivers) && _isEmpty(driversOverrides)) {
    return null;
  } // respect overrides, even if the overrides are an empty array.


  var drivers = driversOverrides || placementDrivers;
  var quickFacts = drivers.map(function (_ref3) {
    var description = _ref3.description,
        label = _ref3.label,
        value = _ref3.value,
        valueTooltip = _ref3.valueTooltip,
        valueTooltipHasHtml = _ref3.valueTooltipHasHtml;
    var valueHelpText;

    if (valueTooltip && valueTooltipHasHtml) {
      /* eslint-disable react/no-danger */
      valueHelpText = /*#__PURE__*/React.createElement("div", {
        dangerouslySetInnerHTML: {
          __html: valueTooltip
        }
      });
      /* eslint-enable react/no-danger */
    } else {
      valueHelpText = valueTooltip;
    }

    return {
      id: _kebabCase(label),
      label: label,
      value: value !== null && value !== void 0 ? value : 'N/A',
      description: safeStructuredContentRender(description),
      valueTooltip: valueHelpText,
      valueSize: 'medium'
    };
  }); // the following quick facts will always be included based on the conditional logic.
  // if the desire is to remove these at any time to actually complete respect an override then
  // just wrap all of this in an if statement checking if using overrides or not.

  if (!shouldOmitCreditScoreDriver) {
    var creditScoreRange = recommendedCreditScoreRangeDetails.creditScoreRange,
        creditScoreRangeDescription = recommendedCreditScoreRangeDetails.description;

    if (showMatchScoreCTA) {
      if (minFico && maxFico) {
        quickFacts.push({
          id: 'recommendedCreditScoreRange',
          label: 'Rec Credit Score',
          value: creditScoreRange || null,
          valueSize: VALUE_SIZES.MEDIUM,
          description: creditScoreRangeDescription
        });
      }
    } else {
      /**
       * all logic taken from
       * https://github.com/NerdWallet/react-components/blob/master/components/quick-facts/src/mappings/credit-cards.jsx#L169
       */
      // eslint-disable-next-line no-lonely-if
      var approvalOdds = approvalLikelihood;
      var loaLabel = 'Recommended Credit Score';
      var loaLabelTooltip;
      var loaValue;
      var loaValueTooltip;
      var creditScore = getCreditScore(identity);

      if (approvalOddsEnabled && approvalOdds && creditScore) {
        var approvalOddsText = getColorAndLabel({
          approvalOdds: approvalOdds
        }).label;
        loaValue = /*#__PURE__*/React.createElement(Impression, {
          className: styles.loaValueWrapper,
          entityName: "cc_approval_odds",
          eventType: "approval_odds_element_impression",
          payload: {
            section_name: analytics === null || analytics === void 0 ? void 0 : analytics.sectionName,
            section_position: analytics === null || analytics === void 0 ? void 0 : analytics.sectionPosition,
            additional_attributes: {
              approval_odds: approvalOddsText
            }
          }
        }, _capitalize(approvalOddsText));
        loaLabel = 'Your approval odds';
        loaValueTooltip = approvalOddsPopoverContent(approvalOdds);
      } else if ((!approvalOdds || !approvalOddsEnabled) && creditScore) {
        // note that @quick-fact-max-width is limiting the full extension of the loavalue for desktop in the current grid layout
        loaValue = /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.createElement("div", {
          className: classes('display-flex')
        }, /*#__PURE__*/React.createElement("div", null, creditScore), /*#__PURE__*/React.createElement("div", null, TRANSUNION_LOGO_CTA)), /*#__PURE__*/React.createElement("div", null, "(".concat(creditScoreRange, ")")));
        loaLabelTooltip = /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.createElement(Type, {
          className: classes(['color-neutral-darkest', 'margin-bottom-2']),
          component: "p",
          size: "1"
        }, "You're", /*#__PURE__*/React.createElement("strong", null, " ".concat(creditScoreComparisonContent === null || creditScoreComparisonContent === void 0 ? void 0 : creditScoreComparisonContent.pointDifference)), " ".concat(creditScoreComparisonContent === null || creditScoreComparisonContent === void 0 ? void 0 : creditScoreComparisonContent.relativeScoreLabel, " suggested score of "), /*#__PURE__*/React.createElement("strong", null, creditScoreComparisonContent === null || creditScoreComparisonContent === void 0 ? void 0 : creditScoreComparisonContent.scoreToCompare), "."), /*#__PURE__*/React.createElement(Type, {
          className: classes(['color-neutral-darkest']),
          component: "p",
          size: "1"
        }, "But, heads up, being within the recommended range doesn\u2019t guarantee approval. Credit score is only one factor lenders consider and they may not use the TransUnion Vantage Score like we do."));
      } else {
        var _isEmpty2;

        var loaTooltipContent = getLoaTooltipContent(identity, approvalOddsEnabled);
        var loaCtaText = approvalOddsEnabled ? SEE_YOUR_APPROVAL_ODDS : GET_YOUR_FREE_CREDIT_SCORE;
        var creditScoreComponent = showCreditScorePill ? /*#__PURE__*/React.createElement(CreditScoreWidget, {
          minFico: minFico,
          maxFico: maxFico,
          limitedCreditHistory: limitedCreditHistory,
          analytics: analytics
        }) : /*#__PURE__*/React.createElement("div", {
          className: classes('display-flex--wide')
        }, /*#__PURE__*/React.createElement("div", {
          className: classes('margin-right-1')
        }, creditScoreRange), /*#__PURE__*/React.createElement("div", null, "(".concat(creditScoreRangeDescription, ")")));
        loaValue = /*#__PURE__*/React.createElement(Fragment, null, creditScoreComponent, /*#__PURE__*/React.createElement(LikelihoodOfApprovalCTA, {
          analytics: analytics === null || analytics === void 0 ? void 0 : analytics.loaCtaProps,
          approvalOddsEnabled: approvalOddsEnabled,
          ctaText: loaCtaText,
          enableAnalytics: (_isEmpty2 = !_isEmpty(analytics)) !== null && _isEmpty2 !== void 0 ? _isEmpty2 : true,
          isKetoCard: true,
          tooltipLabel: loaLabel,
          tooltipContent: loaTooltipContent
        }));
      }

      quickFacts.push({
        id: 'likelihoodOfApproval',
        label: loaLabel,
        labelTooltip: loaLabelTooltip,
        value: loaValue || null,
        valueSize: VALUE_SIZES.MEDIUM,
        valueTooltip: loaValueTooltip
      });
    }
  }

  if (!omitRating && matchScore && showMatchScoreCTA) {
    quickFacts.unshift({
      id: 'starRating',
      label: 'NerdWallet rating',
      value: !showNotYetRated && !_isNumber(starRating) ? null : /*#__PURE__*/React.createElement(GraphQlMarketplaceStarRating, {
        analytics: analytics,
        className: classNames(classes('display-inline-flex')),
        marketplaceEntity: gqlMarketplaceEntity,
        layout: LAYOUTS.SMALL_NUMERIC,
        omitRatingNumberHref: false,
        reviewLinkOverride: reviewLinkOverride,
        showNotYetRated: showNotYetRated,
        trkLocation: TRK_PRODUCT_LOCATION,
        trkPageType: legacyTrkProps === null || legacyTrkProps === void 0 ? void 0 : legacyTrkProps.trkPageType,
        trkTaxonomy: legacyTrkProps === null || legacyTrkProps === void 0 ? void 0 : legacyTrkProps.trkTaxonomy
      }),
      valueSize: VALUE_SIZES.MEDIUM,
      valueTooltip: TOOLTIP_TEXT.credit_cards
    });
  }

  return /*#__PURE__*/React.createElement("div", {
    className: className
  }, /*#__PURE__*/React.createElement(KetoQuickFacts, {
    analytics: analytics,
    enableAnalytics: !_isEmpty(analytics),
    quickFacts: quickFacts
  }));
};

if (process.env.NODE_ENV !== 'production') {
  var _LikelihoodOfApproval, _LikelihoodOfApproval2;

  // eslint-disable-next-line global-require
  var PropTypes = require('prop-types');

  GqlKetoCardDrivers.propTypes = {
    analytics: PropTypes.shape({
      entityName: PropTypes.string,
      sectionName: PropTypes.string,
      sectionPosition: PropTypes.number,
      loaCtaProps: (_LikelihoodOfApproval = LikelihoodOfApprovalCTA === null || LikelihoodOfApprovalCTA === void 0 ? void 0 : (_LikelihoodOfApproval2 = LikelihoodOfApprovalCTA.props) === null || _LikelihoodOfApproval2 === void 0 ? void 0 : _LikelihoodOfApproval2.analytics) !== null && _LikelihoodOfApproval !== void 0 ? _LikelihoodOfApproval : PropTypes.shape({})
    }),
    approvalLikelihood: PropTypes.number,
    approvalOddsEnabled: PropTypes.bool,
    className: PropTypes.string,
    driversOverrides: PropTypes.arrayOf({
      description: PropTypes.string,
      label: PropTypes.string,
      value: PropTypes.string,
      valueTooltip: PropTypes.string,
      valueTooltipHasHtml: PropTypes.bool
    }),
    gqlMarketplaceEntity: PropTypes.shape({
      approvalLikelihood: PropTypes.number,
      creditScoreComparisonContent: PropTypes.shape({
        pointDifference: PropTypes.string,
        relativeScoreLabel: PropTypes.string,
        scoreToCompare: PropTypes.number
      }),
      matchScore: PropTypes.number,
      maxFico: PropTypes.number,
      minFico: PropTypes.number,
      placementDrivers: PropTypes.arrayOf({
        description: PropTypes.string,
        label: PropTypes.string,
        value: PropTypes.string,
        valueTooltip: PropTypes.string,
        valueTooltipHasHtml: PropTypes.bool
      }),
      recommendedCreditScoreRangeDetails: PropTypes.shape({
        creditScoreRange: PropTypes.string,
        description: PropTypes.string
      }),
      starRating: PropTypes.number
    }),
    identity: PropTypes.oneOfType([PropTypes.shape({
      isSignedIn: PropTypes.bool,
      vantageScore3: PropTypes.number
    }), PropTypes.shape({
      isSignedIn: PropTypes.bool,
      profiles: PropTypes.shape({
        tu_credit_report_profile: PropTypes.shape({
          vantage_score_3: PropTypes.number
        })
      })
    })]),
    legacyTrkProps: PropTypes.shape({
      trkPageType: PropTypes.string,
      trkTaxonomy: PropTypes.shape({
        /** Taxonomy page type. */
        pageType: PropTypes.string,

        /** Taxonomy topic. */
        topic: PropTypes.string,

        /** Taxonomy vertical. */
        vertical: PropTypes.string
      })
    }),
    omitRating: PropTypes.bool,
    reviewLinkOverride: PropTypes.string,
    showMatchScoreCTA: PropTypes.bool,
    showNotYetRated: PropTypes.bool,
    shouldOmitCreditScoreDriver: PropTypes.bool
  };
}

GqlKetoCardDrivers.defaultProps = {
  analytics: {}
};
GqlKetoCardDrivers.fragment = gql(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n  fragment GqlKetoCardDrivers on MarketplaceEntityInterface {\n    ... on CreditCardsOfferV2 {\n      creditScoreComparisonContent {\n        relativeScoreLabel\n        scoreToCompare\n        pointDifference\n      }\n      maxFico\n      minFico\n      recommendedCreditScoreRangeDetails {\n        creditScoreRange\n        description\n      }\n    }\n    ... on MarketplaceEntityWithPlacementInterface {\n      placementDrivers {\n        description\n        label\n        value\n\n        ... on EntityPlacementDriver {\n          valueTooltip\n          valueTooltipHasHtml\n        }\n      }\n    }\n    ... on MarketplaceEntityWithStarRatingInterface {\n      starRating\n    }\n    ...MarketplaceStarRating\n  }\n  ", "\n"])), GraphQlMarketplaceStarRating.fragment);

var KetoCardDrivers = function KetoCardDrivers(_ref4) {
  var analytics = _ref4.analytics,
      className = _ref4.className,
      drivers = _ref4.drivers,
      enableAnalytics = _ref4.enableAnalytics,
      wrapDrivers = _ref4.wrapDrivers;
  useDeprecatedPropsWarning({
    component: 'KetoQuickFacts',
    deprecatedProps: [{
      version: '6.9.0',
      deprecated: 'enableAnalytics',
      migration: 'Use analytics instead.'
    }],
    props: {
      enableAnalytics: enableAnalytics
    }
  });

  if (_isEmpty(drivers)) {
    return null;
  }

  return /*#__PURE__*/React.createElement("div", {
    className: className
  }, wrapDrivers ? /*#__PURE__*/React.createElement("div", {
    className: styles.wrappedDrivers
  }, drivers.map(function (driver) {
    return /*#__PURE__*/React.createElement(KetoQuickFacts, {
      analytics: analytics,
      enableAnalytics: enableAnalytics || !_isEmpty(analytics),
      key: driver.id,
      quickFacts: [driver]
    });
  })) : /*#__PURE__*/React.createElement(KetoQuickFacts, {
    analytics: analytics,
    enableAnalytics: enableAnalytics,
    quickFacts: drivers
  }));
};

if (process.env.NODE_ENV !== 'production') {
  // eslint-disable-next-line global-require
  var _PropTypes = require('prop-types');

  KetoCardDrivers.propTypes = {
    analytics: _PropTypes.shape({
      entityName: _PropTypes.string
    }),
    className: _PropTypes.string,
    drivers: _PropTypes.arrayOf(_PropTypes.shape(QuickFacts.PropTypes)),

    /** @deprecated A flag indicating to send analytics */
    enableAnalytics: _PropTypes.bool,
    wrapDrivers: _PropTypes.bool
  };
}

KetoCardDrivers.defaultProps = {
  analytics: {},
  drivers: [],
  wrapDrivers: false
};
export default KetoCardDrivers;